/**
 * Admin auth state.
 *
 * Token is stored in sessionStorage (cleared on tab close).
 * On mount we do a /admin/me probe to confirm the token is still valid.
 */

import { useCallback, useEffect, useState } from "react";
import {
  getAdminToken,
  setAdminToken,
  clearAdminToken,
} from "@/lib/admin-api";
import { adminAuth, type AdminMe } from "@/lib/admin-endpoints";

interface AdminAuthState {
  admin: AdminMe | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

export function useAdminAuth(): AdminAuthState {
  const [admin, setAdmin] = useState<AdminMe | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // On mount, probe /admin/me if a token exists.
  useEffect(() => {
    const token = getAdminToken();
    if (!token) {
      setIsLoading(false);
      return;
    }
    adminAuth
      .me()
      .then(setAdmin)
      .catch(() => {
        clearAdminToken();
      })
      .finally(() => setIsLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await adminAuth.login(email, password);
    setAdminToken(res.token);
    const me = await adminAuth.me();
    setAdmin(me);
  }, []);

  const logout = useCallback(() => {
    clearAdminToken();
    setAdmin(null);
  }, []);

  return {
    admin,
    isLoading,
    isAuthenticated: !!admin,
    login,
    logout,
  };
}
